import { useEffect } from 'react';
import { ArrowLeftRight, FileText, Info } from 'lucide-react';
import { RepairActionCard } from './RepairActionCard';
import { useReconciliation } from '../useReconciliation';
import { reconciliationApi } from '../reconciliation-api';
import type { RepairActionResult } from '../../invoice-repair/types';

export function StepSync() {
  const { diagnostic, dateFrom, dateTo, storeId, updateStepStatus, addRepairLog, isExecuting, setIsExecuting } = useReconciliation();

  const stats = diagnostic?.stats;
  const issues = diagnostic?.issues || [];
  const crossMonthIssue = issues.find(i => i.id === 'cross_month_dates');
  const desyncIssue = issues.find(i => i.id === 'desync_invoices');
  const surplusIssue = issues.find(i => i.id === 'surplus_invoices');
  const unlinkedIssue = issues.find(i => i.id === 'unlinked_in_fakturownia');
  const noOrderIssue = issues.find(i => i.id === 'unlinked_no_order');
  const dbOnlyIssue = issues.find(i => i.id === 'db_only_invoices');

  // Compute step status
  useEffect(() => {
    if (!stats) {
      updateStepStatus('sync', 'idle');
      return;
    }
    const hasCrossMonth = crossMonthIssue && crossMonthIssue.count > 0;
    const hasDesync = (stats as any).desyncInvoices > 0;
    const hasSurplus = surplusIssue && surplusIssue.count > 0;
    const hasUnlinked = unlinkedIssue && unlinkedIssue.count > 0;
    const noOrderInSellPeriod = (noOrderIssue as any)?.countInSellPeriod || 0;
    const hasNoOrder = noOrderInSellPeriod > 0;
    const hasDbOnly = dbOnlyIssue && dbOnlyIssue.count > 0;
    const hasProblems = hasDesync || hasSurplus || hasUnlinked || hasNoOrder || hasDbOnly || stats.duplicateOrders > 0 || hasCrossMonth;
    const synced = stats.fakturowniaVatCount === stats.ecomflowInvoiceCount;
    if (synced && !hasProblems) {
      updateStepStatus('sync', 'done', `${stats.fakturowniaVatCount} faktur zsynchronizowanych`);
    } else {
      const parts: string[] = [];
      if (!synced) parts.push(`EF: ${stats.ecomflowInvoiceCount} ≠ FW: ${stats.fakturowniaVatCount}`);
      if (hasDesync) parts.push(`${(stats as any).desyncInvoices} rozsynchronizowanych`);
      if (hasSurplus) parts.push(`${surplusIssue!.count} nadmiarowych`);
      if (hasUnlinked) parts.push(`${unlinkedIssue!.count} niepowiązanych`);
      if (hasNoOrder) parts.push(`${noOrderInSellPeriod} bez zamówienia`);
      if (hasDbOnly) parts.push(`${dbOnlyIssue!.count} tylko w EcomFlow`);
      if (stats.duplicateOrders > 0) parts.push(`${stats.duplicateOrders} duplikatów`);
      if (hasCrossMonth) parts.push(`${crossMonthIssue!.count} rozbieżnych dat`);
      updateStepStatus('sync', 'warning', parts.join(', '));
    }
  }, [stats, crossMonthIssue, desyncIssue, surplusIssue, unlinkedIssue, noOrderIssue, dbOnlyIssue]);

  if (!diagnostic) {
    return (
      <div className="text-center py-12 text-text-secondary text-sm">
        Wybierz miesiąc i uruchom diagnostykę, aby rozpocząć.
      </div>
    );
  }

  const dateFilterInfo = (diagnostic as any).dateFilterInfo;

  const logAction = (action: string, description: string, result: RepairActionResult) => {
    addRepairLog({
      action,
      description,
      changes: (result.invoices || result.results || []).slice(0, 20).map((inv: any) => ({
        invoiceId: inv.id || inv.invoiceId || '',
        field: inv.oldNumber ? 'number' : 'status',
        oldValue: inv.oldNumber || inv.oldStatus || 'brak',
        newValue: inv.newNumber || inv.newStatus || 'utworzono',
        label: inv.orderNumber || inv.number || '',
      })),
      result: result.failCount && result.failCount > 0 ? 'partial' : 'success',
    });
  };

  const noIssues = !crossMonthIssue?.count && !(stats as any)?.desyncInvoices && !surplusIssue?.count && !unlinkedIssue?.count && !((noOrderIssue as any)?.countInSellPeriod > 0) && !dbOnlyIssue?.count && !stats?.duplicateOrders;

  return (
    <div className="space-y-4">
      {/* Comparison header */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 rounded-xl bg-surface-raised/50 border border-border-subtle">
          <div className="flex items-center gap-2 text-sm text-text-secondary mb-1">
            <FileText className="w-4 h-4" />
            EcomFlow
          </div>
          <div className="text-2xl font-semibold text-text-primary">{stats?.ecomflowInvoiceCount ?? '—'}</div>
          <div className="text-xs text-text-muted">faktur VAT w bazie (po dacie sprzedaży)</div>
          {(stats as any)?.ecomflowOutsidePeriodOrders > 0 && (
            <div className="text-xs text-warning mt-1">
              w tym {(stats as any).ecomflowOutsidePeriodOrders} z zamówieniami spoza tego okresu
            </div>
          )}
          {(stats as any)?.ecomflowOnlyInDb > 0 && (
            <div className="text-xs text-warning mt-1">
              {(stats as any).ecomflowOnlyInDb} brak odpowiednika w Fakturowni
            </div>
          )}
        </div>
        <div className="p-4 rounded-xl bg-surface-raised/50 border border-border-subtle">
          <div className="flex items-center gap-2 text-sm text-text-secondary mb-1">
            <ArrowLeftRight className="w-4 h-4" />
            Fakturownia
          </div>
          <div className="text-2xl font-semibold text-text-primary">{stats?.fakturowniaVatCount ?? '—'}</div>
          <div className="text-xs text-text-muted">
            faktur VAT (po dacie sprzedaży)
            {stats?.fakturowniaCorrectionsCount ? ` + ${stats.fakturowniaCorrectionsCount} korekt` : ''}
          </div>
          {(stats as any)?.crossMonthCount > 0 && (
            <div className="text-xs text-warning mt-1">
              w tym {(stats as any).crossMonthCount} z datą wystawienia w innym miesiącu
            </div>
          )}
          {noOrderIssue && (noOrderIssue as any).countInSellPeriod > 0 && (
            <div className="text-xs text-warning mt-1">
              w tym {(noOrderIssue as any).countInSellPeriod} bez zamówienia w EcomFlow
            </div>
          )}
        </div>
      </div>

      {/* Date filter info */}
      {dateFilterInfo && dateFilterInfo.crossMonth > 0 && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/5 border border-warning/20 text-xs text-text-secondary">
          <Info className="w-3.5 h-3.5 mt-0.5 text-warning flex-shrink-0" />
          <div>
            <strong className="text-warning">{dateFilterInfo.crossMonth} faktur z rozbieżnością dat</strong> — mają datę sprzedaży w tym miesiącu, ale datę wystawienia w innym (np. zamówienie z lutego, faktura wystawiona w marcu). Te faktury są widoczne w diagnostyce, ale nie wliczają się do głównego porównania liczb.
          </div>
        </div>
      )}

      {/* Cross-month date fix */}
      {crossMonthIssue && crossMonthIssue.count > 0 && (
        <RepairActionCard
          title="Rozbieżność dat wystawienia"
          description={`${crossMonthIssue.count} faktur ma datę sprzedaży w tym miesiącu, ale datę wystawienia w innym. Zmiana daty wystawienia na datę sprzedaży zamknie je w jednym okresie.`}
          count={crossMonthIssue.count}
          severity="warning"
          disabled={isExecuting}
          onPreview={() => reconciliationApi.fixIssueDates(true, dateFrom, dateTo, storeId)}
          onExecute={async () => {
            setIsExecuting(true);
            try {
              const res = await reconciliationApi.fixIssueDates(false, dateFrom, dateTo, storeId);
              logAction('fix-issue-dates', `Zmieniono datę wystawienia ${res.count} faktur`, res);
              return res;
            } finally { setIsExecuting(false); }
          }}
        />
      )}

      {/* Desync invoices - sync links from Fakturownia to EcomFlow DB */}
      {desyncIssue && desyncIssue.count > 0 && (
        <RepairActionCard
          title="Rozsynchronizowane faktury"
          description={`${desyncIssue.count} faktur w Fakturowni jest powiązanych z zamówieniami, ale dane w EcomFlow (numer, ID) są nieaktualne. Synchronizacja zaktualizuje dane.`}
          count={desyncIssue.count}
          severity="warning"
          disabled={isExecuting}
          onPreview={() => reconciliationApi.syncLinks(true, dateFrom, dateTo, storeId)}
          onExecute={async () => {
            setIsExecuting(true);
            try {
              const res = await reconciliationApi.syncLinks(false, dateFrom, dateTo, storeId);
              logAction('sync-links', `Zsynchronizowano ${res.count} faktur`, res);
              return res;
            } finally { setIsExecuting(false); }
          }}
        />
      )}

      {/* Truly unlinked invoices - import from Fakturownia */}
      {unlinkedIssue && unlinkedIssue.count > 0 && (
        <RepairActionCard
          title="Niepowiązane faktury w Fakturowni"
          description={`${unlinkedIssue.count} faktur istnieje w Fakturowni, ale nie jest powiązanych z bazą EcomFlow`}
          count={unlinkedIssue.count}
          severity="warning"
          disabled={isExecuting}
          onPreview={() => reconciliationApi.importFromFakturownia(true, dateFrom, dateTo, storeId)}
          onExecute={async () => {
            setIsExecuting(true);
            try {
              const res = await reconciliationApi.importFromFakturownia(false, dateFrom, dateTo, storeId);
              logAction('import-from-fakturownia', `Import ${res.count} faktur z Fakturowni`, res);
              return res;
            } finally { setIsExecuting(false); }
          }}
        />
      )}

      {/* True surplus invoices - delete (only when order has >1 invoice) */}
      {surplusIssue && surplusIssue.count > 0 && (
        <RepairActionCard
          title="Nadmiarowe faktury"
          description={`${surplusIssue.count} faktur w Fakturowni dotyczy zamówień z więcej niż 1 fakturą — prawdziwe duplikaty`}
          count={surplusIssue.count}
          severity="warning"
          destructive
          disabled={isExecuting}
          onPreview={() => reconciliationApi.deleteSurplus(true, dateFrom, dateTo, storeId)}
          onExecute={async () => {
            setIsExecuting(true);
            try {
              const res = await reconciliationApi.deleteSurplus(false, dateFrom, dateTo, storeId);
              logAction('delete-surplus', `Usunięto ${res.count} nadmiarowych faktur`, res);
              return res;
            } finally { setIsExecuting(false); }
          }}
        />
      )}

      {/* Invoices without order — info only, no automated action */}
      {noOrderIssue && noOrderIssue.count > 0 && (
        <div className="p-3 rounded-lg bg-surface-raised/50 border border-border-subtle">
          <div className="flex items-center gap-2 text-sm text-text-secondary mb-2">
            <Info className="w-4 h-4 text-text-secondary" />
            Faktury bez zamówienia ({noOrderIssue.count})
          </div>
          <p className="text-xs text-text-muted mb-2">
            {noOrderIssue.description}
          </p>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {noOrderIssue.details?.map((d: any, i: number) => (
              <div key={i} className="flex items-center justify-between text-xs gap-2">
                <span className="text-text-secondary flex-shrink-0">{d.label}</span>
                <span className="text-text-muted truncate">{d.sublabel}</span>
                {d.badge && (
                  <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] ${
                    d.badgeColor === 'yellow' ? 'bg-warning/10 text-warning' : 'bg-gray-500/10 text-text-muted'
                  }`}>{d.badge}</span>
                )}
                {d.value && <span className="text-text-secondary flex-shrink-0">{d.value}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invoices only in EcomFlow DB — info only */}
      {dbOnlyIssue && dbOnlyIssue.count > 0 && (
        <div className="p-3 rounded-lg bg-surface-raised/50 border border-border-subtle">
          <div className="flex items-center gap-2 text-sm text-text-secondary mb-2">
            <Info className="w-4 h-4 text-text-secondary" />
            Faktury tylko w EcomFlow ({dbOnlyIssue.count})
          </div>
          <p className="text-xs text-text-muted mb-2">
            {dbOnlyIssue.count} faktur jest w bazie EcomFlow, ale nie znaleziono ich w Fakturowni. Mogą to być stare rekordy do weryfikacji.
          </p>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {dbOnlyIssue.details?.map((d: any, i: number) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-text-secondary">{d.label}</span>
                <span className="text-text-muted">{d.sublabel}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sync status - all green */}
      {stats && stats.fakturowniaVatCount === stats.ecomflowInvoiceCount && noIssues && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-success/5 border border-success/20 text-sm text-success">
          <ArrowLeftRight className="w-4 h-4" />
          Synchronizacja OK — liczby faktur się zgadzają
        </div>
      )}

      {/* Duplicate invoices */}
      {stats && stats.duplicateOrders > 0 && (
        <RepairActionCard
          title="Zdublowane faktury"
          description={`${stats.duplicateOrders} zamówień ma więcej niż 1 fakturę VAT`}
          count={stats.duplicateOrders}
          severity="error"
          destructive
          disabled={isExecuting}
          onPreview={() => {
            const prefix = diagnostic.departments[0]?.series[0]?.prefix || 'FV';
            return reconciliationApi.deduplicate(prefix, true, dateFrom, dateTo, storeId);
          }}
          onExecute={async () => {
            setIsExecuting(true);
            try {
              const prefix = diagnostic.departments[0]?.series[0]?.prefix || 'FV';
              const res = await reconciliationApi.deduplicate(prefix, false, dateFrom, dateTo, storeId);
              logAction('deduplicate', `Deduplikacja ${res.count} faktur`, res);
              return res;
            } finally { setIsExecuting(false); }
          }}
        />
      )}

      {/* Orders summary */}
      <div className="p-3 rounded-lg bg-surface-raised/30 border border-border-subtle">
        <div className="text-xs text-text-secondary space-y-1">
          <div className="flex justify-between"><span>Zamówienia w okresie:</span><span className="text-text-secondary">{stats?.totalOrders ?? '—'}</span></div>
          <div className="flex justify-between"><span>Z fakturą:</span><span className="text-text-secondary">{stats?.ordersWithInvoice ?? '—'}</span></div>
          <div className="flex justify-between"><span>Niekwalifikujące się:</span><span className="text-text-secondary">{stats?.notQualifying ?? '—'}</span></div>
        </div>
      </div>
    </div>
  );
}
